import { useRef, useEffect } from 'react';
import PaymentMethod from '@alltrails/analytics/enums/PaymentMethod';
import WebProPurchaseLocation from '@alltrails/analytics/enums/WebProPurchaseLocation';
import proApi from 'api/Pro';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import Coupon from 'types/Coupon';
import CurrencyCode from 'types/CurrencyCode';
import Plan from 'types/Plan';
import logError from 'utils/logError';
import { handlePaymentFormSuccess } from 'utils/Payment';
import { execute } from 'utils/recaptcha_helpers';
import ProResponse from 'types/ProResponse';
import { getPriceTotal } from 'utils/PlanUtils';
import useLanguageCode from 'hooks/useLanguageCode';
import * as styles from './GooglePayButton.module.scss';

type Props = {
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  plan: Plan;
  recurlyPublicKey: string;
  setErrors: (errors: ProResponse['errors']) => void;
  setSubmitting: (submitting: boolean) => void;
  userId: number;
};

// https://www.figma.com/file/YubcZX08vX2JgZf6jlKglQ/Google-pay-with-Recurly
function GooglePay({ coupon, currencyCode, plan, recurlyPublicKey, setErrors, setSubmitting, userId }: Props) {
  const ref = useRef<HTMLDivElement>();

  const languageCode = useLanguageCode();
  const languageRegionCode = useLanguageRegionCode();

  useEffect(() => {
    recurly.configure(recurlyPublicKey);
  }, [recurlyPublicKey]);

  useEffect(() => {
    const googlePay = recurly.GooglePay({
      // see https://developers.google.com/pay/api/web/reference/request-objects#ButtonOptions
      buttonOptions: {
        buttonLocale: languageCode,
        buttonType: 'plain',
        buttonSizeMode: 'fill'
      },
      currency: currencyCode,
      country: 'US', // this is the merchants country which is always US
      total: String(getPriceTotal(plan, coupon)),
      googleMerchantId: 'BCR2DN4TQTTI56B4',
      requireBillingAddress: true
    });

    googlePay.on('ready', button => {
      ref.current.replaceChildren(button);
    });

    googlePay.on('error', error => {
      logError(error);
    });

    googlePay.on('token', async token => {
      try {
        setSubmitting(true);
        const recaptcha = await execute('userPaymentAction');

        const body = {
          recurly_plan_code: plan.recurlyPlanCode,
          google_pay_token: token.id, // Alex implementing under GROW-853
          recaptcha,
          uid: userId,
          user: {
            user_entered_coupon_code: coupon ? coupon.code : null
          }
        };

        const response = await proApi(body);

        if (response?.errors) {
          setSubmitting(false);
          setErrors(response.errors);
        } else {
          handlePaymentFormSuccess(
            userId,
            currencyCode,
            coupon,
            plan,
            languageRegionCode,
            coupon?.code,
            WebProPurchaseLocation.ProPage,
            PaymentMethod.GooglePay
          );
        }
      } catch (e) {
        setSubmitting(false);
        setErrors(e);
      }
    });
  }, [coupon, currencyCode, languageCode, languageRegionCode, plan, setErrors, userId]);

  return (
    <div className={styles.container} ref={ref}>
      {/* show a dummy button until Recurly renders the real Google Pay button client side */}
      <button aria-label="Google Pay" className={styles.button} type="button" />
    </div>
  );
}

export default GooglePay;
